/**
 * Student Hub Context
 * 
 * Global state for Student Hub including:
 * - Academic Level (affects all category pages)
 * - User preferences sync with backend
 * - Level filter resets on logout/user change (uses sessionStorage)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { updateUserPrefs, fetchUserPrefs } from '../services/studentHubApi';
import { useTranslation } from 'react-i18next';

// SessionStorage key for student level (resets on logout/browser close)
export const STUDENT_LEVEL_KEY = 'studentHub.level';
// Key to track last user ID for cross-user filter reset
const LAST_USER_KEY = 'studentHub.lastUserId';

// Academic levels
export const ACADEMIC_LEVELS = [
  { key: 'secondary', label_en: 'Secondary', label_ar: 'ثانوي' },
  { key: 'university', label_en: 'University', label_ar: 'جامعي' },
  { key: 'postgrad', label_en: 'Postgrad', label_ar: 'دراسات عليا' },
  { key: 'vocational', label_en: 'Vocational', label_ar: 'مهني/تقني' },
  { key: 'other', label_en: 'Other', label_ar: 'أخرى' },
];

/**
 * Clear all Student Hub filters (used on logout or user change)
 */
export function clearStudentHubFilters() {
  try {
    sessionStorage.removeItem(STUDENT_LEVEL_KEY);
    sessionStorage.removeItem('studentHub.books.genre');
    // Also clear any legacy localStorage keys
    localStorage.removeItem(STUDENT_LEVEL_KEY);
    localStorage.removeItem('studentHub.books.genre');
  } catch {}
}

const StudentHubContext = createContext(null);

export function StudentHubProvider({ children }) {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const prevUserIdRef = useRef(null);
  
  // Global academic level state - uses sessionStorage for session-only persistence
  const [academicLevel, setAcademicLevel] = useState(() => {
    try {
      // Prefer sessionStorage (session-only)
      const sessionLevel = sessionStorage.getItem(STUDENT_LEVEL_KEY);
      if (sessionLevel) return sessionLevel;
      // Fallback to localStorage for migration
      const localLevel = localStorage.getItem(STUDENT_LEVEL_KEY);
      if (localLevel) {
        sessionStorage.setItem(STUDENT_LEVEL_KEY, localLevel);
        return localLevel;
      }
      return '';
    } catch {
      return '';
    }
  });
  
  // Loading state for level changes
  const [levelLoading, setLevelLoading] = useState(false);
  
  // Clear filters when user ID changes (different user logged in)
  useEffect(() => {
    if (!user?.user_id) return;
    
    const currentUserId = user.user_id;
    const lastUserId = localStorage.getItem(LAST_USER_KEY);
    
    if (lastUserId && lastUserId !== currentUserId) {
      console.log('[StudentHub] User changed, clearing filters');
      clearStudentHubFilters();
      setAcademicLevel('');
    }
    
    localStorage.setItem(LAST_USER_KEY, currentUserId);
    prevUserIdRef.current = currentUserId;
  }, [user?.user_id]);
  
  // Sync with backend on mount if user is logged in
  useEffect(() => {
    if (user && token) {
      fetchUserPrefs(token)
        .then(prefs => {
          if (prefs.academicLevel) {
            setAcademicLevel(prefs.academicLevel);
            sessionStorage.setItem(STUDENT_LEVEL_KEY, prefs.academicLevel);
          }
        })
        .catch(err => console.error('Failed to fetch user prefs:', err));
    }
  }, [user, token]);
  
  // Update academic level globally
  const updateAcademicLevel = useCallback(async (newLevel) => {
    const level = newLevel === 'all' || newLevel === 'other' ? '' : newLevel;
    
    // Optimistic update
    setAcademicLevel(level);
    setLevelLoading(true);
    
    // Persist to sessionStorage (session-only)
    try {
      if (level) {
        sessionStorage.setItem(STUDENT_LEVEL_KEY, level);
      } else {
        sessionStorage.removeItem(STUDENT_LEVEL_KEY);
      }
    } catch {}
    
    // Sync to backend if logged in
    if (token && level) {
      try {
        await updateUserPrefs(token, { academicLevel: level });
      } catch (err) {
        console.error('Failed to sync level:', err);
      }
    }
    
    setLevelLoading(false);
    
    return level;
  }, [token]);
  
  // Get level label for display
  const getLevelLabel = useCallback((key, isRTL = false) => {
    if (!key || key === 'other') {
      return t('bfs.allLevels');
    }
    const level = ACADEMIC_LEVELS.find(l => l.key === key);
    if (!level) return t('shc.level');
    return isRTL ? level.label_ar : level.label_en;
  }, []);
  
  // Effective level for API calls (empty string = all levels)
  const effectiveLevel = academicLevel === 'other' ? '' : academicLevel;
  
  const value = {
    academicLevel,
    effectiveLevel,
    levelLoading,
    updateAcademicLevel,
    getLevelLabel,
    ACADEMIC_LEVELS,
    STUDENT_LEVEL_KEY,
    clearStudentHubFilters,
  };
  
  return (
    <StudentHubContext.Provider value={value}>
      {children}
    </StudentHubContext.Provider>
  );
}

export function useStudentHub() {
  const context = useContext(StudentHubContext);
  if (!context) {
    throw new Error('useStudentHub must be used within a StudentHubProvider');
  }
  return context;
}

export default StudentHubContext;
